import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiCall } from '../Apis/apiCall';
import { handleApiResponse } from '../utils/response';
import { usePermissions } from '../context/PermissionContext';
import Breadcrumb from '../components/Breadcrumb';
import { CancelButtonForm, UpdateButtonForm } from '../components/ui/Button';
import Shimmer from '../components/ui/Shimmer';

const EditClientPermissionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [editPermission, setEditPermission] = useState({
    permission_key: '',
    description: '',
    module_name: '',
  });
  const [fetchLoading, setFetchLoading] = useState(false); // For initial data fetch
  const [submitLoading, setSubmitLoading] = useState(false); // For form submission
  const [errorMessage, setErrorMessage] = useState('');

  const getAuthToken = () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setErrorMessage('Please log in to continue.');
    }
    return token;
  };

  const fetchPermissionById = async () => {
    try {
      setFetchLoading(true);
      setErrorMessage('');
      const token = getAuthToken();
      if (!token) {
        setErrorMessage('Authentication token missing. Please log in.');
        setFetchLoading(false);
        return;
      }

      const response = await apiCall('get', `/get-client-permission/${id}`, null, navigate);

      if (response?.data?.success) {
        const permission = response.data.data;
        setEditPermission({
          permission_key: permission.permission_key,
          description: permission.description,
          module_name: permission.module_name,
        });
        handleApiResponse(response.data, 'success', 'GET');
      } else {
        setErrorMessage(response?.data?.message || 'Failed to load permission details.');
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, 'Err', 'GET');
      setErrorMessage(error.message || 'Error fetching permission details.');
    } finally {
      setFetchLoading(false);
    }
  };

  const updatePermission = async (e) => {
    e.preventDefault();
    try {
      setSubmitLoading(true);
      const response = await apiCall('put', `/update-client-permission/${id}`, editPermission, navigate);

      if (response?.data?.success) {
        handleApiResponse(response.data, 'success', 'PUT');
        navigate('/client-permissions');
      } else {
        setErrorMessage('Failed to update permission.');
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, 'Err', 'PUT');
      setErrorMessage(error.message || 'Error updating permission.');
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (hasPermission('update_client_permission')) {
      fetchPermissionById();
    } else {
      setErrorMessage('You do not have permission to edit this resource.');
    }
  }, [id]);

  const isFormValid = () => {
    return editPermission.permission_key.trim() !== "" && editPermission.module_name.trim() !== "";
  };

  if (!hasPermission('update_client_permission') && !fetchLoading) {
    return (
      <div className="p-4">
        <div className="alert alert-danger">
          You do not have permission to edit this resource.
        </div>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb title="Edit Permission" />

      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage('')}
              ></button>
            </div>
          )}

          {fetchLoading ? (
            <Shimmer type="modal" />
          ) : (
            <form onSubmit={updatePermission} autoComplete="off">
              <div className="row g-20">
                <div className="col-md-6 mb-20">
                  <label htmlFor="permissionKey" className="form-label">
                    Permission Key *
                  </label>
                  <input
                    id="permissionKey"
                    type="text"
                    className="form-control radius-8"
                    value={editPermission.permission_key}
                    onChange={(e) =>
                      setEditPermission({
                        ...editPermission,
                        permission_key: e.target.value,
                      })
                    }
                    placeholder="Enter permission key"
                    disabled={!hasPermission('update_client_permission')}
                    required
                  />
                </div>

                <div className="col-md-6 mb-20">
                  <label htmlFor="description" className="form-label">
                    Description
                  </label>
                  <input
                    id="description"
                    type="text"
                    className="form-control radius-8"
                    value={editPermission.description}
                    onChange={(e) =>
                      setEditPermission({
                        ...editPermission,
                        description: e.target.value,
                      })
                    }
                    placeholder="Enter description"
                    disabled={!hasPermission('update_client_permission')}
                  />
                </div>

                <div className="col-md-6 mb-20">
                  <label htmlFor="moduleName" className="form-label">
                    Module Name *
                  </label>
                  <input
                    id="moduleName"
                    type="text"
                    className="form-control radius-8"
                    value={editPermission.module_name}
                    onChange={(e) =>
                      setEditPermission({
                        ...editPermission,
                        module_name: e.target.value,
                      })
                    }
                    placeholder="Enter module name"
                    disabled={!hasPermission('update_client_permission')}
                    required
                  />
                </div>
              </div>

              <div className="d-flex justify-content-center gap-3">
                <CancelButtonForm />
                {hasPermission('update_client_permission') && (
                  <UpdateButtonForm
                    disabled={submitLoading || !isFormValid()}
                    text={submitLoading ? "Please wait..." : "Save Permission"}
                  />
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default EditClientPermissionPage;